<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  isAvailabilityConfigurableField,
  fieldEnforcesAvailability,
  isBookingDatetimeField,
  isEventSpaceField,
  ALL_SLOT_OPTIONS,
  normalizeSlotOptions,
} from 'dashboard/helper/preChat';

const props = defineProps({
  field: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  isOnCanvas: {
    type: Boolean,
    default: false,
  },
  hideBookingOrderTip: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'toggleRequired',
  'updateLabel',
  'updatePlaceholder',
  'updateSelectionMode',
  'updateSlotOptions',
  'updateEnforceAvailability',
  'remove',
]);

const { t } = useI18n();

const fieldTypeLabel = computed(() => {
  const typeMap = {
    standard: t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.FIELD_TYPES.STANDARD'),
    contact_attribute: t(
      'INBOX_MGMT.PRE_CHAT_FORM.BUILDER.FIELD_TYPES.CONTACT'
    ),
    conversation_attribute: t(
      'INBOX_MGMT.PRE_CHAT_FORM.BUILDER.FIELD_TYPES.CONVERSATION'
    ),
    event_attribute: t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.FIELD_TYPES.EVENT'),
  };
  return typeMap[props.field.field_type] || props.field.field_type;
});

const fieldSourceIcon = computed(() => {
  const map = {
    standard: 'i-lucide-star',
    contact_attribute: 'i-lucide-contact',
    conversation_attribute: 'i-lucide-message-circle',
    event_attribute: 'i-lucide-calendar',
  };
  return map[props.field.field_type] || 'i-lucide-star';
});

const fieldSourceTooltip = computed(() => fieldTypeLabel.value);

const typeIconClass = computed(() => {
  const map = {
    text: 'i-lucide-type',
    textarea: 'i-lucide-align-left',
    email: 'i-lucide-mail',
    number: 'i-lucide-hash',
    link: 'i-lucide-link',
    date: 'i-lucide-calendar',
    time: 'i-lucide-clock',
    booking_datetime: 'i-lucide-calendar-clock',
    list: 'i-lucide-list',
    checkbox: 'i-lucide-circle-check',
    currency: 'i-lucide-dollar-sign',
    percent: 'i-lucide-percent',
    select: 'i-lucide-chevron-down',
    space: 'i-lucide-map-pin',
    spaces: 'i-lucide-map-pin',
  };
  return map[props.field.type] || 'i-lucide-type';
});

const typeTooltip = computed(() => {
  const type = props.field.type || 'text';
  if (type === 'space' || type === 'spaces') {
    return 'Space';
  }
  if (type === 'booking_datetime') {
    return 'Date & time';
  }
  return type.charAt(0).toUpperCase() + type.slice(1);
});

const isEnabled = computed(() => props.field.enabled);
const isRequired = computed(() => props.field.required);
const isRemovable = computed(() => props.field.name !== 'message');
const isSpaceField = computed(
  () =>
    props.field.name === 'space_id' ||
    props.field.name === 'event_space_id' ||
    props.field.type === 'space' ||
    props.field.type === 'spaces'
);
const isDatetimeField = computed(() => isBookingDatetimeField(props.field));
const isPaxField = computed(() => props.field.name === 'pax');
const showBookingOrderTip = computed(
  () =>
    !props.hideBookingOrderTip &&
    (isPaxField.value ||
      isEventSpaceField(props.field) ||
      isBookingDatetimeField(props.field))
);
const showEnforceAvailability = computed(() =>
  isAvailabilityConfigurableField(props.field)
);
const enforcesAvailability = computed(() =>
  fieldEnforcesAvailability(props.field)
);
const enforceToggleLabel = computed(() =>
  isPaxField.value
    ? t('INBOX_MGMT.PRE_CHAT_FORM.ENFORCE_CAPACITY.LABEL')
    : t('INBOX_MGMT.PRE_CHAT_FORM.ENFORCE_AVAILABILITY.LABEL')
);
const enforceToggleHelp = computed(() =>
  isPaxField.value
    ? t('INBOX_MGMT.PRE_CHAT_FORM.ENFORCE_CAPACITY.HELP')
    : t('INBOX_MGMT.PRE_CHAT_FORM.ENFORCE_AVAILABILITY.HELP')
);
const isMultiSelect = computed(() => props.field.selection_mode === 'multi');
const selectedSlotOptions = computed(() =>
  normalizeSlotOptions(props.field.slot_options)
);
const hidePlaceholder = computed(() => props.field.type === 'booking_datetime');

const slotOptionLabel = id => {
  const key = String(id).toUpperCase();
  return t(`INBOX_MGMT.PRE_CHAT_FORM.SLOT_OPTIONS.${key}`);
};

const isSlotOptionChecked = id => selectedSlotOptions.value.includes(id);

const toggleSlotOption = id => {
  const current = selectedSlotOptions.value;
  const next = current.includes(id)
    ? current.filter(x => x !== id)
    : [...current, id];
  // Keep at least one option
  if (!next.length) return;
  emit('updateSlotOptions', props.field, normalizeSlotOptions(next));
};
</script>

<template>
  <div
    v-if="compact"
    class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab transition-colors duration-150 group hover:bg-neutral-50"
    :class="
      isOnCanvas ? 'text-n-slate-11' : 'hover:bg-n-alpha-1 text-n-slate-12'
    "
  >
    <span class="i-lucide-grip-vertical text-n-slate-9 size-4 shrink-0" />
    <span
      v-tooltip="typeTooltip"
      :class="typeIconClass"
      class="size-4 shrink-0 text-n-slate-10"
    />
    <span class="truncate text-sm flex-1">{{ field.label || field.name }}</span>
    <span
      v-if="isOnCanvas"
      class="i-lucide-check size-4 text-n-teal-9 shrink-0"
    />
  </div>

  <div
    v-else
    class="flex flex-col gap-3 rounded-lg bg-white dark:bg-n-solid-2 outline outline-1 outline-n-container p-3 transition-all duration-200 hover:outline-n-brand group"
  >
    <div class="flex items-center gap-2">
      <span
        class="drag-handle i-lucide-grip-vertical text-n-slate-9 size-4 shrink-0 cursor-grab"
      />
      <span
        v-tooltip="typeTooltip"
        :class="typeIconClass"
        class="size-4 shrink-0 text-n-slate-10"
      />
      <input
        type="text"
        :value="field.label"
        :placeholder="field.name"
        class="reset-base flex-1 min-w-0 text-sm font-medium bg-transparent text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
        @input="emit('updateLabel', field, $event.target.value)"
      />
      <div className="flex flex-row gap-4">
        <span
          v-tooltip="fieldSourceTooltip"
          class="flex gap-1 justify-center items-center px-1.5 py-1 rounded-md shadow outline-1 outline outline-n-container bg-n-solid-2 text-xs text-neutral-400 self-center"
        >
          <span :class="fieldSourceIcon" class="size-3 text-neutral-400" />
          {{ field.name }}
        </span>
        <div class="flex items-center gap-3 ml-auto">
          <label
            v-if="isSpaceField"
            class="flex items-center gap-1.5 text-xs text-n-slate-10 cursor-pointer select-none"
          >
            <ToggleSwitch
              :model-value="isMultiSelect"
              @change="
                emit(
                  'updateSelectionMode',
                  field,
                  isMultiSelect ? 'single' : 'multi'
                )
              "
            />
            {{ t('INBOX_MGMT.PRE_CHAT_FORM.SPACE_SELECTION_MODE.LABEL') }}
          </label>
          <label
            v-if="showEnforceAvailability"
            v-tooltip="enforceToggleHelp"
            class="flex items-center gap-1.5 text-xs text-n-slate-10 cursor-pointer select-none"
          >
            <ToggleSwitch
              :model-value="enforcesAvailability"
              @change="
                emit('updateEnforceAvailability', field, !enforcesAvailability)
              "
            />
            {{ enforceToggleLabel }}
          </label>
          <label
            class="flex items-center gap-1.5 text-xs text-n-slate-10 cursor-pointer select-none"
          >
            <ToggleSwitch
              :model-value="isRequired"
              @change="emit('toggleRequired', field)"
            />
            {{ t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.REQUIRED') }}
          </label>
        </div>
        <NextButton
          v-if="isRemovable"
          icon="i-lucide-trash-2"
          size="sm"
          variant="ghost"
          color="ruby"
          class="opacity-0 group-hover:opacity-100 transition-opacity"
          @click="emit('remove', field)"
        />
      </div>
    </div>

    <div v-if="isEnabled" class="flex flex-col gap-2 pl-7">
      <p
        v-if="field.name === 'message' && !isRequired"
        class="text-xs text-n-slate-11"
      >
        {{ t('INBOX_MGMT.PRE_CHAT_FORM.MESSAGE_FIELD.OPTIONAL_HELP') }}
      </p>
      <p v-if="showBookingOrderTip" class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.PRE_CHAT_FORM.BOOKING_ORDER.HELP') }}
      </p>
      <div v-if="isDatetimeField" class="flex flex-col gap-1.5">
        <span class="text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.SLOT_OPTIONS.LABEL') }}
        </span>
        <p class="text-xs text-n-slate-10">
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.SLOT_OPTIONS.HELP') }}
        </p>
        <div class="flex flex-wrap gap-x-3 gap-y-1.5">
          <label
            v-for="optId in ALL_SLOT_OPTIONS"
            :key="optId"
            class="flex items-center gap-1.5 text-xs text-n-slate-12 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              class="rounded border-n-weak"
              :checked="isSlotOptionChecked(optId)"
              @change="toggleSlotOption(optId)"
            />
            {{ slotOptionLabel(optId) }}
          </label>
        </div>
      </div>
      <input
        v-if="!hidePlaceholder"
        type="text"
        :value="field.placeholder"
        :placeholder="
          t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.PLACE_HOLDER')
        "
        class="reset-base text-sm rounded-lg px-2.5 py-1.5 bg-n-alpha-1 outline outline-1 outline-n-container text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand transition-colors"
        @input="emit('updatePlaceholder', field, $event.target.value)"
      />
    </div>
  </div>
</template>
